import { Button } from '@spiaggeit/spit-ui'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom'

import { useWhiteLabelBgTextClassName } from '@/hooks/useWhiteLabelBgTextClassName.ts'
import { licenseSlice } from '@/store/licenseSlice'
import { getSpotsPageByBookingMode } from '@/utils/getSpotsPageByBookingMode'
import { TICKETS_PATH } from '../../app/router/paths'
import { Card } from '../../components/Card'
import { useAppDispatch, useAppSelector } from '../../hooks/store'
import { BookingAvailability } from '../../store/bookingAvailabilitySlice'
import { cartSlice } from '../../store/cartSlice'

interface Props {
  areSpotsAvailable: boolean
  areTicketsAvailable: boolean
}

export const ChooseProductDefault = (props: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const licenseCode = useAppSelector(licenseSlice.selectors.licenseCode)
  const license = useAppSelector(licenseSlice.selectors.license)
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()

  const textClassName = useWhiteLabelBgTextClassName(
    'hidden min-w-0 text-center md:block'
  )
  const headingClassName = useWhiteLabelBgTextClassName(
    'mb-4 text-lg font-bold'
  )

  if (!license) return null
  const spotsPage = getSpotsPageByBookingMode(license?.bookingMode)

  return (
    <main className="container grow pb-16 pt-8 md:max-w-[50rem] md:py-20">
      <h2 className={headingClassName}>{t('chooseProduct.title')}</h2>

      <div className="flex flex-col gap-x-8 gap-y-4 md:flex-row md:items-center md:justify-between">
        <Card
          action={
            <Button
              disabled={!props.areSpotsAvailable}
              fullWidth
              onClick={() => {
                dispatch(
                  cartSlice.actions.setBookingType(BookingAvailability.SPOTS)
                )
                navigate({
                  pathname: generatePath(spotsPage, { license: licenseCode }),
                  search: searchParams.toString(),
                })
              }}
              type="button"
            >
              {t('chooseProduct.spot.book')}
            </Button>
          }
          isSoldOut={!props.areSpotsAvailable}
          list={[
            t('chooseProduct.spot.listItem00'),
            t('chooseProduct.spot.listItem01'),
            t('chooseProduct.spot.listItem02'),
          ]}
          title={t('chooseProduct.spot.title')}
        />

        <div className={textClassName}>{t('chooseProduct.or')}</div>

        <Card
          action={
            <Button
              color="primary"
              disabled={!props.areTicketsAvailable}
              fullWidth
              onClick={() => {
                navigate({
                  pathname: generatePath(TICKETS_PATH, {
                    license: licenseCode,
                  }),
                  search: searchParams.toString(),
                })
                dispatch(
                  cartSlice.actions.setBookingType(BookingAvailability.TICKETS)
                )
              }}
              type="button"
              variant="outline"
            >
              {t('chooseProduct.ticket.book')}
            </Button>
          }
          isSoldOut={!props.areTicketsAvailable}
          list={[
            t('chooseProduct.ticket.listItem00'),
            t('chooseProduct.ticket.listItem01'),
            t('chooseProduct.ticket.listItem02'),
          ]}
          title={t('chooseProduct.ticket.title')}
        />
      </div>
    </main>
  )
}
